import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Role } from '@pgway/shared';

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
}

const navItems: Record<Role, NavItem[]> = {
  admin: [
    { path: '/admin/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
    { path: '/admin/companies', labelKey: 'nav.companies', icon: 'C' },
    { path: '/admin/sellers', labelKey: 'nav.sellers', icon: 'S' },
    { path: '/admin/api-config', labelKey: 'nav.apiConfig', icon: 'A' },
    { path: '/admin/sync', labelKey: 'nav.sync', icon: 'Y' },
  ],
  seller: [
    { path: '/seller/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
    { path: '/seller/sub-sellers', labelKey: 'nav.subSellers', icon: 'U' },
    { path: '/seller/stats', labelKey: 'nav.stats', icon: 'T' },
    { path: '/seller/payouts', labelKey: 'nav.payouts', icon: 'P' },
  ],
  'sub-seller': [
    { path: '/sub-seller/dashboard', labelKey: 'nav.dashboard', icon: 'H' },
    { path: '/sub-seller/stats', labelKey: 'nav.stats', icon: 'T' },
    { path: '/sub-seller/payouts', labelKey: 'nav.payouts', icon: 'P' },
  ],
};

interface SidebarProps {
  role: Role;
}

export function Sidebar({ role }: SidebarProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const items = navItems[role] || [];

  return (
    <aside
      className={`flex h-screen flex-col bg-blue-900 dark:bg-gray-950 transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Header / logo area — full primary color */}
      <div
        className={`flex h-14 items-center bg-blue-950 dark:bg-black ${
          collapsed ? 'justify-center px-0' : 'justify-between px-4'
        }`}
      >
        {!collapsed && (
          <span className="text-sm font-bold text-white tracking-wide">Seller Manager</span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="rounded p-1 text-blue-300 hover:bg-blue-800 hover:text-white transition-colors"
          aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        >
          {collapsed ? '>>' : '<<'}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-l-4 border-blue-400 bg-blue-800 text-white dark:border-blue-400 dark:bg-gray-800 dark:text-blue-300'
                  : 'text-blue-200 hover:bg-blue-800 hover:text-white dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
              }`
            }
          >
            <span className="flex h-6 w-6 items-center justify-center text-xs font-bold flex-shrink-0">
              {item.icon}
            </span>
            {!collapsed && <span className="ml-3">{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Footer role label */}
      <div className="border-t border-blue-800 dark:border-gray-800 px-4 py-3">
        {!collapsed && (
          <div className="text-xs text-blue-400 dark:text-gray-500">
            {t('nav.role')}: {t(`roles.${role}`)}
          </div>
        )}
      </div>
    </aside>
  );
}
